import { FC, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { SubscriptionPackage } from '@bpm-web-app/supreme-api-sdk';
import { getPlatformLinkUsingRouter, getSignupRedirectLink, rebuildReactTooltip, useUserSettings } from '@bpm-web-app/utils';
import { useGetMeSubscription } from '@bpm-web-app/swr-hooks';
import { EventType, SignUpStep, trackEvent } from 'libs/utils/src/lib/trackEvent';
import { Account, AccountPageProps } from '../../account';
import { PageHeader } from '../../components/page-header/page-header';
import { Card } from '../card/card';
import { LibrarySwitch } from '../library-switch/library-switch';
import { libraryFeaturesData, useLibraryData } from './use-library-data';
import { StaticContent } from './static-content/static-content';
import { BillingPeriodToggle } from '../billing-period-toggle';
import accountStyles from '../../account.module.css';
import styles from '../plans.module.css';
import { usePlans } from './use-plans';
import { useBillingPeriod } from './use-billing-period';

type Props = AccountPageProps;

export const AccountPlansSupreme: FC<Props> = ({ title }) => {
    const { packages } = usePlans('supreme');
    const { isAnonymous } = useUserSettings();
    const [period, setPeriod] = useBillingPeriod();
    const [toggleDisabled, setToggleDisabled] = useState(false);
    const { data: currentSubscription } = useGetMeSubscription('supreme');
    const { library, setLibrary, selected } = useLibraryData();

    const subscriptions = packages?.data
        .filter((d) => {
            if (!(d.library === library || d.library === SubscriptionPackage.LibraryEnum.All)) {
                return false;
            }
            if (period === '1') {
                if (d.trial_period === 12) {
                    return false;
                }
                if (d.period === 1) {
                    return true;
                }
                return false;
            }
            if (period === '12') {
                if (d.period === 12) {
                    return true;
                }
                if (d.trial_period === 12) {
                    return true;
                }
            }
            return false;
        })
        .sort((a, b) => Number(a.amount) - Number(b.amount));

    useEffect(() => {
        if (subscriptions && subscriptions.length === 0 && period === '1') {
            setPeriod('12');
            setToggleDisabled(true);
        } else if (subscriptions && subscriptions.length > 0 && period === '1') {
            setToggleDisabled(false);
        }
        rebuildReactTooltip();
    }, [subscriptions, period, setPeriod, setToggleDisabled]);

    useEffect(() => {
        trackEvent({
            type: EventType.signUpStep,
            data: {
                platform: SubscriptionPackage.PlatformEnum.Supreme,
                step: SignUpStep.plansPage
            }
        });
    }, []);

    const getLibraryPlanData = useCallback((subscriptionPackage: SubscriptionPackage) => {
        if (subscriptionPackage.library === SubscriptionPackage.LibraryEnum.All) {
            return libraryFeaturesData.supreme.all;
        }
        const libKey = library === SubscriptionPackage.LibraryEnum.Latin ? 'latin' : 'openFormat';
        const planKey = subscriptionPackage.name.toString().toLowerCase() as keyof typeof libraryFeaturesData.supreme.openFormat;
        return libraryFeaturesData.supreme[libKey][planKey] ?? libraryFeaturesData.supreme.openFormat[planKey];
    }, [library]);

    const getFormattedFeatures = useCallback((subscriptionPackage: SubscriptionPackage) => {
        return getLibraryPlanData(subscriptionPackage)?.features ?? [];
    }, [getLibraryPlanData]);

    const getListTitle = useCallback((subscriptionPackage: SubscriptionPackage) => {
        return getLibraryPlanData(subscriptionPackage)?.listTitle ?? '';
    }, [getLibraryPlanData]);

    return (
        <Account title={title} fluid>
            <div className={classNames(accountStyles['account__layout'], accountStyles['account__layout--wide'])}>
                <PageHeader title={title} hasBackButton={!isAnonymous} />
                <LibrarySwitch value={library} onChange={setLibrary} />

                <div className={styles['plans__header']}>
                    <p className={styles['plans__description']}>{selected?.description}</p>
                    <BillingPeriodToggle
                        value={period}
                        onChange={setPeriod}
                        disabled={toggleDisabled || !packages?.data.filter((s) => (s.period === 12 || s.trial_period === 12) && s.info?.allowed !== false).length}
                    />
                </div>

                <div className={styles['plans__cards']}>
                    {subscriptions?.map((subscription, i, s) => {
                        const cardLink = isAnonymous
                            ? getSignupRedirectLink(`/account/plan/change/${subscription.id}?backRoute=${encodeURIComponent(getPlatformLinkUsingRouter('/account/overview'))}`)
                            : `./plan/change/${subscription.id}`;
                        const payNowPromo = subscription.info?.promoted && subscription.info.promoted_amount && subscription.info?.promoted_amount !== '0.00' ? `Pay $${subscription.info.promoted_amount} today` : '';

                        let { amount } = subscription;
                        let periodStr = period === '1' || subscription.period === 1 ? 'mo' : 'yr';
                        let subTitle: string | undefined;
                        let strikeThroughText: string | undefined;
                        let hidePeriodSlash = false;

                        if (subscription.info?.trial_allowed && subscription.trial_period && subscription.trial_amount) {
                            periodStr = `${subscription.trial_period === 12 ? 'One Year' : subscription.trial_period === 1 ? 'First Month' : `${subscription.trial_period} Months*`}`;
                            strikeThroughText = subscription.trial_period === 12 ? `$${Number(subscription.info.trial_discount)}/12 Months` : `$${Number(subscription.amount)}/${subscription.period} Month${subscription.period > 1 ? 's' : ''}`;
                            amount = subscription.trial_amount;
                            if (subscription.period === 12) {
                                subTitle = `*$${subscription.amount}/year thereafter`;
                            } else {
                                subTitle = `*$${subscription.amount}/mo thereafter`;
                            }
                        } else if (subscription.info?.promoted && subscription.info.promoted_amount) {
                            strikeThroughText = `$${Number(subscription.amount)}/${subscription.period} ${subscription.period === 1 ? 'Month' : 'Months'}`;
                            amount = subscription.info.promoted_amount;
                            periodStr = ' now';
                            hidePeriodSlash = true;
                            if (subscription.period === 12) {
                                subTitle = `*$${subscription.amount}/year thereafter`;
                            } else {
                                subTitle = `*$${subscription.amount}/mo thereafter`;
                            }
                        }

                        return (
                            <Card
                                subscriptionPackage={subscription}
                                currentMembership={currentSubscription?.data.membership}
                                {...subscription}
                                amount={amount}
                                strikeThroughText={strikeThroughText}
                                subTitle={subTitle}
                                key={subscription.id}
                                customLibrary={subscription.library === SubscriptionPackage.LibraryEnum.All ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}><span>Both Libraries + Royalty-Free Sounds</span>
                                    </div>
                                ) : undefined}
                                library={selected?.title}
                                disabled={subscription.is_current}
                                period={periodStr}
                                hidePeriodSlash={hidePeriodSlash}
                                link={cardLink}
                                listTitle={getListTitle(subscription)}
                                title={(subscription.display_title || subscription.name).toString()}
                                theme={(subscription.library === SubscriptionPackage.LibraryEnum.All ? 'all' : subscription.name).toString().toLowerCase()}
                                features={getFormattedFeatures(subscription)}
                                tag={(subscription.is_promotion) ? subscription.promotion_text : payNowPromo}
                            />
                        );
                    }
                    )}
                </div>
            </div>

            <StaticContent
                onSelectPlan={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
            />
        </Account>
    );
};
